import { TFunction } from 'i18next';
import { getModelNameLabel } from './ChatTypeHelpers';
import {
  AnomalyDirection,
  AnomalyIncident,
  AnomalyModelEntry,
  AnomalyPromptEntry,
  ContributorType,
} from '../models/anomaly-detection/models/AnomalyIncident';
import { AnomalyMetricKey } from '../models/anomaly-detection/models/AnomalyIncidentsResponse';

type AnomalyTooltipParams = {
  value: [string, number];
  data?: Partial<AnomalyIncident> & {
    valueUnit?: string;
    metric?: AnomalyMetricKey;
    metricName?: string;
    verbPositive?: string;
    verbNegative?: string;
  };
};

type ContributorEntry = {
  type: ContributorType;
  value: number;
};

const CONTRIBUTOR_TYPE_ORDER: ContributorType[] = ['changed', 'added', 'deleted'];

const getValueSortDirection = (
  type: ContributorType,
  metric: AnomalyMetricKey,
  direction: AnomalyDirection,
): 'asc' | 'desc' => {
  if (type === 'deleted') {
    if (metric === 'brand_score') {
      return direction === 'positive' ? 'asc' : 'desc';
    }

    return direction === 'positive' ? 'desc' : 'asc';
  }

  if (metric === 'brand_score') {
    return direction === 'positive' ? 'desc' : 'asc';
  }

  return direction === 'positive' ? 'asc' : 'desc';
};

const sortContributorEntries = <T extends ContributorEntry>(
  entries: T[],
  metric: AnomalyMetricKey,
  direction: AnomalyDirection,
): T[] => {
  return [...entries].sort((left, right) => {
    const leftTypeOrder = CONTRIBUTOR_TYPE_ORDER.indexOf(left.type);
    const rightTypeOrder = CONTRIBUTOR_TYPE_ORDER.indexOf(right.type);

    if (leftTypeOrder !== rightTypeOrder) {
      return leftTypeOrder - rightTypeOrder;
    }

    if (left.type === 'changed') {
      return Math.abs(right.value) - Math.abs(left.value);
    }

    const sortDirection = getValueSortDirection(left.type, metric, direction);
    return sortDirection === 'asc' ? left.value - right.value : right.value - left.value;
  });
};
 
const formatDeltaValue = (value: number, direction?: AnomalyDirection): string => {
  const prefix = direction === 'positive' ? '+' : direction === 'negative' ? '-' : '';
  const valueAbs = Math.abs(Number(value.toFixed(2)));
  return `${prefix}${valueAbs}`;
};

const getAccentColor = (direction: AnomalyDirection | undefined): string => {
  if (direction === 'positive') return 'var(--green-100)';
  if (direction === 'negative') return 'var(--red-300)';
  return 'var(--grey-ux-400)';
};

const getDirectionIcon = (direction: AnomalyDirection | undefined): string => {
  if (direction === 'positive') return '<span class="ico ico--xs ico--chart-up"></span>';
  if (direction === 'negative') return '<span class="ico ico--xs ico--chart-down"></span>';
  return '–';
};

const getContributorIcon = (type: ContributorType): string => {
  if (type === 'deleted') return "ico--minus";
  if (type === 'added') return "ico--plus";
  return "ico--activity";
};


const buildSectionLabel = (label: string): string =>
  `<div class="anomaly-tooltip__section-label">${label}</div>`;

const buildBulletList = (items: string[]): string => {
  if (!items.length) return `<div class="anomaly-tooltip__empty">–</div>`;
  return `<ul class="anomaly-tooltip__list">${items
    .map((item) => `<li class="anomaly-tooltip__list-item">${item}</li>`)
    .join('')}</ul>`;
};

const buildPromptItems = (
  prompts: AnomalyPromptEntry[] | undefined,
  unit: string,
  direction: AnomalyDirection,
  metric: AnomalyMetricKey | undefined,
  accent?: string,
): string[] => {
  if (!prompts?.length) return [];
  const sorted = metric
    ? sortContributorEntries(prompts, metric, direction)
    : [...prompts].sort(
        (left, right) =>
          CONTRIBUTOR_TYPE_ORDER.indexOf(left.type) - CONTRIBUTOR_TYPE_ORDER.indexOf(right.type),
      );
  return sorted.map((prompt) => {
    return buildListItem(prompt.prompt, prompt.type, prompt.value, unit, direction, accent);
  });
};

const buildModelItems = (
  models: AnomalyModelEntry[] | undefined,
  unit: string,
  direction: AnomalyDirection,
  metric: AnomalyMetricKey | undefined,
  accent?: string,
): string[] => {
  if (!models?.length) return [];
  const sorted = metric
    ? sortContributorEntries(models, metric, direction)
    : [...models].sort(
        (left, right) =>
          CONTRIBUTOR_TYPE_ORDER.indexOf(left.type) - CONTRIBUTOR_TYPE_ORDER.indexOf(right.type),
      );
  return sorted.map((model) => {
    const label = getModelNameLabel(model.model) || model.model;
    return buildListItem(label, model.type, model.value, unit, direction, accent);
  });
};

const buildListItem = (
  label: string,
  type: ContributorType,
  value: number,
  unit: string,
  direction?: AnomalyDirection,
  accent?: string,
): string => {
  const icon = getContributorIcon(type);
  const valueStr = type === 'changed'
      ? `${formatDeltaValue(value, direction)}${unit}`
      : `${Math.abs(Number(value.toFixed(2)))}${unit}`;
  return `<span class="anomaly-tooltip__contrib-row"><span class="ico ico--xs ${icon}" style="background-color:${accent}"></span><span class="anomaly-tooltip__contrib-label">${label}</span><strong class="anomaly-tooltip__contrib-value">(${valueStr})</strong></span>`;
};

export const formatAnomalyIncidentTooltip = (
  params: AnomalyTooltipParams,
  t: TFunction,
  dateFormatter?: (raw: string) => string,
): string => {
  const chartDate = params.value[0];
  const incident = params.data ?? {};
  const endDate = incident.endDate ?? chartDate;
  const rawRange =
    incident.startDate && incident.startDate !== endDate
      ? `${incident.startDate} - ${endDate}`
      : endDate;
  const periodLabel = dateFormatter ? dateFormatter(rawRange) : rawRange;

  const accent = getAccentColor(incident.direction);
  const icon = getDirectionIcon(incident.direction);
  const unit = incident.valueUnit ?? '';
  const metricName = incident.metricName ?? '';
  const verb = incident.direction === 'positive'
    ? (incident.verbPositive ?? t('anomalyHeadlineTo'))
    : (incident.verbNegative ?? t('anomalyHeadlineTo'));

  const formatVal = (v: number): string => `${v}${unit}`;
  const currentVal = incident.currentValue != null ? formatVal(incident.currentValue) : '–';
  const baselineVal = incident.baselineValue != null ? formatVal(incident.baselineValue) : '–';
  const delta =
    incident.currentValue != null && incident.baselineValue != null
      ? `${formatDeltaValue(incident.currentValue - incident.baselineValue, incident.direction)}${unit}`
      : null;

  const headline = metricName
    ? `${metricName} ${verb} ${baselineVal} ${t('anomalyHeadlineTo')} ${currentVal}`
    : `${baselineVal} → ${currentVal}`;

  const promptItems = incident.direction
    ? buildPromptItems(incident.topChangePrompts, unit, incident.direction, incident.metric, accent)
    : [];
  const modelItems = incident.direction
    ? buildModelItems(incident.topChangeModels, unit, incident.direction, incident.metric, accent)
    : [];

  return `
<div class="anomaly-tooltip">
  <div class="anomaly-tooltip__accent" style="background:${accent}"></div>
  <div class="anomaly-tooltip__header">
    <div class="anomaly-tooltip__direction">
      <span class="anomaly-tooltip__direction-icon">${icon}</span>
      <span class="anomaly-tooltip__direction-label" style="color:${accent}">${delta ?? '–'}</span>
    </div>
    <span class="anomaly-tooltip__date">${periodLabel}</span>
  </div>
  <div class="anomaly-tooltip__body">
    <p class="anomaly-tooltip__headline">${headline}</p>
    ${
      promptItems.length
        ? `<div class="anomaly-tooltip__section">
      ${buildSectionLabel(t('anomalyTooltipTopPrompts'))}
      ${buildBulletList(promptItems)}
    </div>`
        : ''
    }
    ${
      modelItems.length
        ? `<div class="anomaly-tooltip__section">
      ${buildSectionLabel(t('anomalyTooltipTopModels'))}
      ${buildBulletList(modelItems)}
    </div>`
        : ''
    }
  </div>
</div>`;
};
